import React from 'react';
import { CheckoutConfig, CheckoutAddon } from '../../../types';
import { CURRENCIES } from '../../../constants';

interface AddonsTabProps {
  config: CheckoutConfig;
  onUpdate: (partial: Partial<CheckoutConfig>) => void;
}

export const CheckoutStudioAddonsTab: React.FC<AddonsTabProps> = ({ config, onUpdate }) => {
  const handleAddAddon = () => {
    const newAddon: CheckoutAddon = {
      id: `addon-${Date.now()}`,
      name: 'Priority VIP Support',
      price: 29,
      currency: 'USD',
      description: '24/7 Dedicated Slack channel and fast-track resolution.',
      isEnabled: true,
    };
    onUpdate({ addons: [...(config.addons || []), newAddon] });
  };

  const handleUpdateAddon = (index: number, partial: Partial<CheckoutAddon>) => {
    const updated = [...(config.addons || [])];
    updated[index] = { ...updated[index], ...partial };
    onUpdate({ addons: updated });
  };

  const handleDeleteAddon = (id: string) => {
    onUpdate({ addons: (config.addons || []).filter((a) => a.id !== id) });
  };

  return (
    <div className="space-y-4 text-left">
      {/* Billing Discounts */}
      <div className="p-3.5 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200/80 dark:border-slate-800 space-y-3">
        <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Billing Cycle Discount Incentives</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase">Quarterly Discount (%)</label>
            <input
              type="number"
              value={config.quarterlyDiscountPercentage || 0}
              onChange={(e) => onUpdate({ quarterlyDiscountPercentage: Number(e.target.value) })}
              className="w-full text-xs font-bold px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold text-slate-500 uppercase">Yearly Discount (%)</label>
            <input
              type="number"
              value={config.yearlyDiscountPercentage || 0}
              onChange={(e) => onUpdate({ yearlyDiscountPercentage: Number(e.target.value) })}
              className="w-full text-xs font-bold px-3 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg outline-none"
            />
          </div>
        </div>
      </div>

      {/* Add-on Upsells List */}
      <div className="p-3.5 bg-slate-50 dark:bg-slate-900/60 rounded-xl border border-slate-200/80 dark:border-slate-800 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs font-bold text-slate-800 dark:text-slate-200">Order Add-on Upsells</p>
            <p className="text-[11px] text-slate-500">Allow customers to attach extra options at checkout.</p>
          </div>
          <button
            type="button"
            onClick={handleAddAddon}
            className="px-3 py-1 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs font-bold transition-all"
          >
            + Add Addon
          </button>
        </div>

        <div className="space-y-2.5">
          {(config.addons || []).map((addon, idx) => {
            const sym = CURRENCIES.find(c => c.code === addon.currency)?.symbol || '$';
            return (
              <div
                key={addon.id}
                className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 space-y-2"
              >
                <div className="flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
                  <input
                    type="text"
                    value={addon.name}
                    onChange={(e) => handleUpdateAddon(idx, { name: e.target.value })}
                    placeholder="Addon title"
                    className="flex-1 text-xs font-bold px-2.5 py-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded outline-none"
                  />
                  <select
                    value={addon.currency || 'USD'}
                    onChange={(e) => handleUpdateAddon(idx, { currency: e.target.value })}
                    className="text-xs font-bold px-2 py-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded outline-none"
                  >
                    {CURRENCIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.code}
                      </option>
                    ))}
                  </select>
                  <div className="flex items-center gap-1">
                    <span className="text-xs font-bold text-slate-400">{sym}</span>
                    <input
                      type="number"
                      value={addon.price}
                      onChange={(e) => handleUpdateAddon(idx, { price: Number(e.target.value) })}
                      className="w-16 text-xs font-bold px-2 py-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleUpdateAddon(idx, { isEnabled: !addon.isEnabled })}
                    className={`px-2 py-1 text-[10px] font-bold rounded ${
                      addon.isEnabled ? 'bg-emerald-100 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300' : 'bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300'
                    }`}
                  >
                    {addon.isEnabled ? 'Active' : 'Disabled'}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDeleteAddon(addon.id)}
                    className="p-1 text-slate-400 hover:text-rose-600"
                  >
                    <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>

                <input
                  type="text"
                  value={addon.description || ''}
                  onChange={(e) => handleUpdateAddon(idx, { description: e.target.value })}
                  placeholder="Short benefit description"
                  className="w-full text-xs px-2.5 py-1 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded outline-none"
                />
              </div>
            );
          })}

          {(config.addons || []).length === 0 && (
            <p className="text-xs text-slate-400 italic py-2 text-center">No add-ons created yet. Click "+ Add Addon" above.</p>
          )}
        </div>
      </div>
    </div>
  );
};
